/**
 * Şablon editörü — the reference's `data-screen="editor"` (`Screens.editor`, `m08-admin.js:174`).
 *
 * Ported against the rendered DOM: the page head with its status and «Kaydedilmedi» pills and its
 * eight actions, the two-column `.editor` grid, the stage with its fit calculation, overlay and
 * one `.layer-box` per resolved layer, the test-data strip, the fit notice, the server-render
 * panel and the two dialogs. The property panel is `screens/editor/PropsPanel.tsx`.
 *
 * Three of the reference's decisions look like faults until you read it, and all three are kept:
 *
 *   - the working layout is a DEEP COPY of the template's draft (`stripDraft`), so an unsaved
 *     experiment cannot reach the list screen. «Taslak kaydet» is the only thing that writes back.
 *   - the drag path does NOT re-render the stage. `updateStageLive` (`m08-admin.js:253`) moves the
 *     existing boxes and redraws the background in place, because re-rendering the stage under the
 *     pointer is what upstream is avoiding. Here that means the layer object is mutated live, the
 *     boxes and the background are written through refs, and the readout, the fit notice and the
 *     property panel's four geometry inputs are refreshed the same way — a `live` subscription for
 *     the first two, `syncPropsFromLayer`'s own `[data-l]` writes for the third. The reducer is
 *     told only once, at pointerup.
 *   - `ED` is module-level upstream (`m08-admin.js:164`), so leaving the editor and returning to
 *     the SAME template keeps the unsaved working copy — `Screens.editor` re-seeds only when
 *     `ED.tplId` changed. `EDITOR_CACHE` below is that cell, written from the reducer with the
 *     value it just returned, which stays correct under StrictMode's double invocation.
 *
 * Every mutation happens in the event handler and the reducer only records it, so the reducer
 * stays pure: `mutate(fn)` is upstream's `mut` — snapshot, mutate in place, tell the reducer.
 */
import {
  type CSSProperties,
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAdoptTemplateCounts } from '../data/adopt';
import { useV07Commands } from '../data/commands-context';
import { useV07Queries } from '../data/queries-context';
import { useTemplateEmail } from '../data/template-email';
import { type LinkedInCardDesign, useTemplateLinkedInCard } from '../data/template-linkedin-card';
import { CertificateEmailPanel } from './editor/CertificateEmailPanel';
import { LINKEDIN_CARD_FIT, LinkedInCardPanel } from './editor/LinkedInCardPanel';
import { useV07Account } from '../data/session';
import { useDemoData, useTemplate } from '../data/store';
import type { DemoDraft, DemoTemplate } from '../data/types';
import { useRead } from '../data/use-read';
import type { LibraryFont } from '../lib/asset-library';
import {
  asRenderLayout,
  defaultTestData,
  editsDeclaredLanguage,
  holderFieldKey,
  layerById,
  localizedEditorLayout,
  makeBackground,
  setLocaleBackground,
  stripDraft,
  TEST_NAMES,
  TEST_ORGS,
} from '../lib/editor-layout';
import { clearPendingDraft, readPendingDraftRecord, writePendingDraft } from '../lib/pending-intent';
import { localeLabel } from '../lib/sealed-locales';
import {
  currentRelease,
  fieldLabel,
  fmtDateTime,
  fmtNum,
  isLocalizedDateField,
  localizedDateText,
  TEMPLATE_STATUS_TR,
} from '../lib/reference';
import {
  type BoxGeo,
  type Canvas,
  canvasH,
  canvasW,
  type Fit,
  fitText,
  type Layer,
  type Layout,
  layoutFonts,
  ordered,
  previewSVG,
  qrMatrix,
  type RenderData,
  resolveAssetRef,
  resolveValue,
  sizePx,
  type TextGeo,
  typeRec,
} from '../lib/render';
import type { RenderAsset } from '../lib/render/library';
import { duplicateTemplateRecord, tplCount } from '../lib/template-list';
import { Modal, ModalHead } from '../ui/Modal';
import { useToast } from '../ui/toast';
import {
  clamp,
  type EditorLayer,
  type EditorLayout,
  type EditorState,
  type EditorTab,
  INITIAL_EDITOR_STATE,
  NUDGE_STEP,
  NUDGE_STEP_SHIFT,
  snapLayer,
} from './editor/contract';
import { type LibraryReadState, PropsPanel } from './editor/PropsPanel';
import { PublishGate } from './editor/PublishGate';
import { ServerRenderOut } from './editor/ServerRenderOut';
import { TemplatePreview } from './editor/TemplatePreview';

// ------------------------------------------------------------------ the engine's two internals

/**
 * `Engine.resolve` and `Engine.geometry` (`m04-engine.js:81` and `:90`) are module-private in
 * `lib/render/engine.ts`: the fail-closed plan is what every other surface consumes, and only the
 * editor needs the raw resolution — the boxes are drawn from it, and the fit notice reports
 * `floor`, `refError` and the resolved asset's own metadata, none of which a `PlanItem` carries.
 *
 * They are re-ported here rather than exported from the engine because that file belongs to
 * another part of this phase. Every primitive underneath them (`ordered`, `resolveValue`,
 * `fitText`, `qrMatrix`, `resolveAssetRef`) is the engine's own, so there is one implementation of
 * the arithmetic and two callers of it.
 */
interface ResolvedQr {
  layer: EditorLayer;
  kind: 'qr';
  text: string;
  matrix: boolean[][] | null;
}

interface ResolvedImage {
  layer: EditorLayer;
  kind: 'image';
  asset: RenderAsset | null;
  refError: string | null;
}

/** Upstream's text resolution also carries `valueSource` / `valueMissing` / `fieldDef`; the plan
 *  reads those, the editor never does, so they are left to `resolveRenderPlan`. */
interface ResolvedText extends Fit {
  layer: EditorLayer;
  kind: 'text';
}

type Resolved = ResolvedQr | ResolvedImage | ResolvedText;

/** The contract's `EditorLayer` and the engine's `Layer` are one object under two spellings, as
 *  `asRenderLayout` is for layouts (`lib/editor-layout.ts`). */
const asRenderLayer = (l: EditorLayer): Layer => l as unknown as Layer;

function resolveLayers(layout: EditorLayout, data: RenderData): Resolved[] {
  const canvas = layout.canvas as Canvas;
  const rendered = asRenderLayout(layout);
  const byId = new Map(layout.layers.map((l) => [l.id, l]));
  return ordered(rendered).map((raw): Resolved => {
    /* `ordered` only filters and sorts the layout's own layers, so the lookup always hits; the
       fallback keeps the expression total. */
    const l = byId.get(raw.id) ?? (raw as unknown as EditorLayer);
    if (l.kind === 'qr') {
      const fromData = data.verification_url || (l.fieldKey !== undefined ? data[l.fieldKey] : undefined);
      const def = (layout.fieldDefinitions || []).find((f) => f.key === l.fieldKey);
      const url = String(fromData || def?.sample || '');
      return { layer: l, kind: 'qr', text: url, matrix: qrMatrix(url) };
    }
    if (l.kind === 'image') {
      const r = l.assetRef ? resolveAssetRef(l.assetRef) : ({ ok: false, reason: 'missing_ref' } as const);
      return { layer: l, kind: 'image', asset: r.ok ? r.asset : null, refError: r.ok ? null : r.reason };
    }
    const rv = resolveValue(rendered, asRenderLayer(l), data);
    /* The stage always draws a DRAFT — «Taslak kaydet» is the only thing that writes back, and a
       template opened on a published release is copied into a new draft first (`stripDraft`). So
       the case fold follows the language this draft declares, live, which is also what gets pinned
       into the layers when it is published. Nothing already published is redrawn by this. */
    return {
      layer: l,
      kind: 'text',
      ...fitText(asRenderLayer(l), rv.text, canvas, layout.defaultLanguage),
    };
  });
}

/** `Engine.geometry`, the QR/image half: the square centred on the layer's point. */
function boxGeometryOf(canvas: Canvas, l: EditorLayer): BoxGeo {
  const W = canvasW(canvas);
  const H = canvasH(canvas);
  const w = (l.width ?? 0.1) * W;
  const h = l.height ? l.height * H : w;
  return { x: l.x * W - w / 2, y: l.y * H - h / 2, size: w, w, h };
}

/** `Engine.geometry`, the text half. `.35 × size` is the reference's baseline offset in the line box. */
function textGeometryOf(canvas: Canvas, l: EditorLayer, r: Fit): TextGeo {
  const W = canvasW(canvas);
  const H = canvasH(canvas);
  const y = l.y * H;
  const lh = r.size * (l.lineHeight || 1.15);
  const blockH = r.lines.length * lh;
  const top = l.verticalAlign === 'top' ? y : l.verticalAlign === 'bottom' ? y - blockH : y - blockH / 2;
  return {
    x: l.x * W,
    y,
    lh,
    top,
    blockH,
    baselines: r.lines.map((_, i) => top + lh * (i + 0.5) + r.size * 0.35),
    anchor: l.align === 'left' ? 'start' : l.align === 'right' ? 'end' : 'middle',
  };
}

/** The box the editor draws, which is NOT the render geometry — `boxGeometry`, `m08-admin.js:226`.
 *  A text box is the layer's full width and at least 40px tall, and it sits 9px above the block. */
interface EditorBox {
  left: number;
  top: number;
  w: number;
  h: number;
}

function editorBoxGeometry(layout: EditorLayout, r: Resolved): EditorBox {
  const canvas = layout.canvas as Canvas;
  const l = r.layer;
  if (r.kind === 'qr' || r.kind === 'image') {
    const g = boxGeometryOf(canvas, l);
    return { left: g.x, top: g.y, w: g.w, h: g.h };
  }
  const W = canvasW(canvas);
  const w = l.width * W;
  const h = Math.max(r.lines.length * r.size * (l.lineHeight || 1.15) + 18, 40);
  const left = l.align === 'left' ? l.x * W : l.align === 'right' ? l.x * W - w : l.x * W - w / 2;
  return { left, top: textGeometryOf(canvas, l, r).top - 9, w, h };
}

// ------------------------------------------------------------------------------- font readiness

type FontStatusCode = 'loaded' | 'loading' | 'failed' | 'webfont_no_asset' | 'fallback';

interface FontRow {
  family: string;
  status: FontStatusCode;
  digests: string[];
  licensed: boolean;
}

/**
 * `evaluateFontReadiness` (`m01b-assets.js:480`) over `allUsedFontFamilies` (`m10-router.js:158`).
 *
 * Both seeded Poppins faces are registered from the reference's own bytes by `styles/fonts.css`,
 * so a family the library holds is `loaded` on this surface for the same reason it is upstream —
 * the face is actually there. A family with no faces falls back to the reference's web-font probe,
 * which needs BOTH `document.fonts.check` and a measurable metric difference from monospace AND
 * serif; without both the answer is `fallback`, which blocks canonical output.
 */
function fontReadiness(templates: DemoTemplate[], faces: LibraryFont[]): FontRow[] {
  const families = new Set<string>(['Poppins']);
  for (const t of templates) {
    for (const lay of [t.draft, ...t.releases]) {
      for (const f of layoutFonts(lay as unknown as Layout)) if (f) families.add(String(f));
    }
  }
  for (const f of faces) families.add(f.family);

  return [...families].map((family): FontRow => {
    const own = faces.filter((f) => f.family === family);
    if (own.length) {
      return {
        family,
        status: own.every((f) => f.state === 'loaded')
          ? 'loaded'
          : own.some((f) => f.state === 'failed')
            ? 'failed'
            : 'loading',
        digests: own.map((f) => f.sha256.slice(0, 12)),
        licensed: own.every((f) => f.licenseAcknowledged),
      };
    }
    let checked = false;
    try {
      checked = document.fonts?.check(`600 20px "${family}"`) ?? false;
    } catch {
      checked = false;
    }
    let differs = false;
    try {
      const cv = document.createElement('canvas').getContext('2d');
      if (cv) {
        const S = 'HPWİğüşçö 0123456789';
        cv.font = `600 48px "${family}", monospace`;
        const a = cv.measureText(S).width;
        cv.font = '600 48px monospace';
        const b = cv.measureText(S).width;
        cv.font = `600 48px "${family}", serif`;
        const c = cv.measureText(S).width;
        differs = Math.abs(a - b) > 0.5 && Math.abs(a - c) > 0.5;
      }
    } catch {
      differs = false;
    }
    return {
      family,
      status: checked && differs ? 'webfont_no_asset' : 'fallback',
      digests: [],
      licensed: false,
    };
  });
}

/**
 * `fontStatusHTML` — `m01-utils.js:326`, in its own order of severity.
 *
 * Upstream's first branch, «Font kontrol ediliyor…», cannot occur here: readiness is computed from
 * a synchronous library dump rather than awaited, so there is no moment with no answer.
 */
function FontStatusPill({ rows }: { rows: FontRow[] }): ReactElement {
  if (!rows.length) return <span className="pill pill-neutral none">Font tanımlı değil</span>;
  const bad = rows.filter((r) => r.status === 'fallback' || r.status === 'failed');
  if (bad.length) {
    /*
     * WHICH of the two things is wrong, because they are fixed in completely different places.
     *
     * A family with no registered face at all means this installation has not had its fonts set up
     * yet: the binaries deliberately do not ship inside the product (ADR-003 §Font zorunluluğu —
     * they are uploaded through `font.create`), so a fresh deployment starts with an empty font
     * library and the family select falls back to its placeholder. «Fallback font» described that
     * as a typeface problem and sent an owner looking for a broken font file; what the console was
     * actually reporting was an empty library, and the fix is «Varlıklar → Font yükle».
     *
     * A family that HAS registered faces and could not load them is the other thing, and keeps the
     * wording it had. Neither is any less blocking, and neither decision changed.
     */
    const noAsset = bad.every((r) => r.digests.length === 0);
    return (
      <span className="pill pill-danger none" title={bad.map((r) => r.family).join(', ')}>
        {noAsset
          ? "Kayıtlı font asset'i yok — canonical export engellendi"
          : 'Fallback font — canonical export engellendi'}
      </span>
    );
  }
  const unverified = rows.filter((r) => r.status === 'webfont_no_asset');
  if (unverified.length) {
    return (
      <span className="pill pill-pending none" title={unverified.map((r) => r.family).join(', ')}>
        {"Web fontu · asset digest'i yok"}
      </span>
    );
  }
  if (rows.some((r) => r.status === 'loading')) {
    return <span className="pill pill-neutral none">Font yükleniyor…</span>;
  }
  const unlicensed = rows.filter((r) => !r.licensed);
  return (
    <span
      className="pill pill-active none"
      title={rows.map((r) => `${r.family}: ${r.digests.join('/')}`).join(' · ')}
    >
      {`Font asset digest'leriyle hazır${unlicensed.length ? ' · lisans teyidi yok' : ''}`}
    </span>
  );
}

// ------------------------------------------------------------------------------ the live channel

/**
 * What `updateStageLive` pokes so the readout and the fit notice refresh mid-drag without the
 * screen re-rendering — the whole point of upstream's live path is that the stage is NOT rebuilt
 * while the pointer is down. The layer object is mutated in place, so a subscriber that re-reads
 * it sees the new geometry; this channel only tells it to look again.
 */
interface LiveChannel {
  subscribe: (cb: () => void) => () => void;
  getSnapshot: () => number;
  bump: () => void;
}

function createLiveChannel(): LiveChannel {
  let version = 0;
  const subscribers = new Set<() => void>();
  return {
    subscribe: (cb) => {
      subscribers.add(cb);
      return () => {
        subscribers.delete(cb);
      };
    },
    getSnapshot: () => version,
    bump: () => {
      version += 1;
      for (const cb of [...subscribers]) cb();
    },
  };
}

// ------------------------------------------------------------------------------------- the state

const previewData = (
  testData: Record<string, string>,
  /**
   * The working language and the layout it is read from — passed only by the editor's own stage.
   *
   * `credential_title` is not a literal on a layer: it is the AUTO field the release fills from its
   * canonical title, so translating it is not a layer edit. Left out, the stage would go on drawing
   * the Turkish name under «English» — the one substitution this whole feature exists to prevent.
   * A language whose title is empty draws an empty title here, which is what the publish gate
   * refuses and what the Şablon tab names.
   */
  locale?: { lang: 'tr' | 'en'; layout: EditorLayout },
): RenderData => {
  /* `previewData` — `m08-admin.js:170`. The QR renders whatever the verification field holds, so
     the three candidate keys collapse onto `verification_url` before anything resolves. */
  const d: RenderData = { ...testData };
  d.verification_url = d.verification_qr || d.credential_url || d.verification_url;
  if (locale) {
    /*
     * A DATE IS DRAWN IN THE LANGUAGE BEING EDITED.
     *
     * `testData` keeps exactly what is in the box — the field's own sample, or whatever the author
     * typed — so switching the language never resets their work and the box never changes under them.
     * What changes is the TEXT the stage draws: the value is read back to the day it names and written
     * in this language's words. A value that is not a date is drawn as it stands, so a free-text
     * answer keeps its own words and nothing is replaced by today.
     *
     * Before this, the stage drew the stored sample verbatim, and the catalogue's sample for
     * «Veriliş Tarihi» is the Turkish words «2 Eylül 2026» — which is why the English tab showed a
     * Turkish date.
     */
    /* The server's own set, not every `date` field: see `isLocalizedDateField`. A template's own
       date field is drawn exactly as the sealed document will draw it. */
    for (const f of locale.layout.fieldDefinitions ?? []) {
      if (!isLocalizedDateField(f.key)) continue;
      const held = d[f.key];
      if (held === undefined || held === null) continue;
      d[f.key] = localizedDateText(held, locale.lang);
    }
    const said = locale.layout.localizedMetadata?.[locale.lang]?.title;
    /*
     * The DECLARED language keeps whatever was seeded, because that is what the release freezes as
     * its canonical title and what the document actually draws — `defaultTestData` already put it
     * there, name-of-the-template fallback included. A SECOND language with no title of its own
     * draws nothing, which is exactly what the publish gate refuses it for; filling it from the
     * declared language would be the substitution this whole feature exists to prevent.
     */
    if (said !== undefined && said.trim() !== '') d.credential_title = said;
    else if (locale.lang !== locale.layout.defaultLanguage) d.credential_title = '';
  }
  return d;
};

interface DerivedSync {
  scale: number;
  propsKey: string;
}

type EditorAction =
  | { type: 'load'; state: EditorState }
  | { type: 'select'; id: string | null }
  | { type: 'tab'; tab: EditorTab }
  | { type: 'edit_locale'; locale: 'tr' | 'en' }
  | { type: 'zoom'; zoom: 'fit' | number }
  | { type: 'toggle'; key: 'guides' | 'safe' | 'grid' | 'snap'; value: boolean }
  | { type: 'aspect_lock'; value: boolean }
  | { type: 'test_data'; key: string; value: string }
  | { type: 'push_undo'; before: EditorLayout }
  | { type: 'mutated'; before: EditorLayout }
  | { type: 'commit' }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'saved' }
  | { type: 'derived'; derived: DerivedSync };

/** `pushUndo` — `m08-admin.js:171`: push, then drop the oldest past sixty. */
function pushSnapshot(stack: EditorLayout[], snapshot: EditorLayout): EditorLayout[] {
  const next = [...stack, snapshot];
  if (next.length > 60) next.shift();
  return next;
}

/** `doUndo` / `doRedo` — the selection falls back to the first layer when the popped layout has
 *  no such layer any more, and both mark the working copy dirty. */
function restore(
  state: EditorState,
  layout: EditorLayout,
): Pick<EditorState, 'layout' | 'sel' | 'dirty' | 'propsKey'> {
  const sel = layerById(layout, state.sel) ? state.sel : (layout.layers[0]?.id ?? null);
  return { layout, sel, dirty: true, propsKey: '' };
}

function reduce(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'load':
      return action.state;
    case 'select':
      return { ...state, sel: action.id };
    case 'tab':
      return { ...state, tab: action.tab, propsKey: '' };
    /* The working language. `propsKey` is cleared because the panel's STRUCTURE changes with it —
       a different language's boxes — and it does NOT touch `dirty`: looking is not editing. */
    case 'edit_locale':
      return { ...state, editLocale: action.locale, propsKey: '' };
    case 'zoom':
      return { ...state, zoom: action.zoom };
    case 'toggle':
      return { ...state, [action.key]: action.value };
    case 'aspect_lock':
      return { ...state, aspectLock: action.value };
    case 'test_data':
      return { ...state, testData: { ...state.testData, [action.key]: action.value } };
    case 'push_undo':
      return { ...state, undo: pushSnapshot(state.undo, action.before), redo: [] };
    case 'mutated':
      /* The handler already mutated the working layout in place, as `mut` does; the new identity
         is what tells React the stage and the panel have to be drawn again. */
      return {
        ...state,
        undo: pushSnapshot(state.undo, action.before),
        redo: [],
        layout: state.layout === null ? null : { ...state.layout },
        dirty: true,
        propsKey: '',
      };
    case 'commit':
      return { ...state, layout: state.layout === null ? null : { ...state.layout }, dirty: true };
    case 'undo': {
      const previous = state.undo[state.undo.length - 1];
      if (!previous || state.layout === null) return state;
      return {
        ...state,
        undo: state.undo.slice(0, -1),
        redo: pushSnapshot(state.redo, structuredClone(state.layout)),
        ...restore(state, previous),
      };
    }
    case 'redo': {
      const next = state.redo[state.redo.length - 1];
      if (!next || state.layout === null) return state;
      return {
        ...state,
        redo: state.redo.slice(0, -1),
        undo: pushSnapshot(state.undo, structuredClone(state.layout)),
        ...restore(state, next),
      };
    }
    case 'saved':
      return { ...state, dirty: false };
    case 'derived':
      return { ...state, scale: action.derived.scale, propsKey: action.derived.propsKey };
  }
}

/**
 * `ED` — `m08-admin.js:164`, module-level so the working copy outlives one visit to the screen.
 * The reducer mirrors the value it just returned into this cell, which is idempotent and so stays
 * correct when StrictMode invokes it twice.
 */
/** Which of the two copies an editing session is about. */
export type DraftSourceChoice = 'server' | 'recovered';

interface EditorMemory {
  /** WHOSE unsaved work this is. `null` is the demo, which has no session and no second user. */
  ownerKey: string | null;
  /** The wizard's design, when the request that should have stored it did not. */
  recovered: DemoTemplate | null;
  /** When that design was made, so it can be compared with the server's draft rather than guessed. */
  recoveredAt: string | null;
  /** The working copy, so leaving the screen and coming back does not restart the editing session. */
  editor: EditorState;
  /**
   * WHICH copy that working session is about.
   *
   * Without it the cache was picked on `tplId` alone, so it could only ever hand back whichever
   * session happened to be in memory — and the screen's choice between the server's draft and the
   * recovery was silently overruled one layer down. Pressing «Sunucudaki taslağı aç» then opened the
   * recovery anyway, and saving from there would have written it over the server's newer work.
   */
  editorSource: DraftSourceChoice | null;
  /**
   * The LinkedIn share arrangement the author has not saved yet, and which certificate it is for.
   *
   * It sat in the editor's own `useState`, which survives closing the panel and nothing else:
   * walking to Şablonlar and back unmounted the editor and the layout went with it, silently. It
   * belongs in the same cell as the certificate's working copy — bound to an account, bound to a
   * template — so it outlives the visit exactly as that one does, and so it is dropped, not
   * shown, when the account changes.
   */
  linkedInDraft: LinkedInCardDrafts;
  linkedInDraftTplId: string | null;
}

/**
 * One unsaved share arrangement per language.
 *
 * A certificate sealed in two languages has two share images — the picture embeds that language's
 * certificate — and each is saved on its own. So the unsaved copy is per language too: switching the
 * language must not carry the Turkish arrangement into the English one, and saving one must leave the
 * other exactly as it was.
 */
export type LinkedInCardDrafts = Partial<Record<'tr' | 'en', LinkedInCardDesign>>;

const EMPTY_MEMORY: EditorMemory = {
  ownerKey: null,
  recovered: null,
  recoveredAt: null,
  editor: INITIAL_EDITOR_STATE,
  editorSource: null,
  linkedInDraft: {},
  linkedInDraftTplId: null,
};

/**
 * `ED` — `m08-admin.js:164`, module-level so the working copy outlives one visit to the screen.
 *
 * It carries an owner because it is unsaved work, and unsaved work belongs to somebody. Without
 * that, the storage copy was carefully bound to a user and the MEMORY copy walked straight past it:
 * signing out and in as a colleague, in the same tab, handed them the previous person's design as
 * the editor's starting point — and their «Taslak Kaydet» would have written it to the server under
 * their name. The storage reader was never the whole boundary; this is the rest of it.
 */
let MEMORY: EditorMemory = EMPTY_MEMORY;

/** Everything this module remembers, dropped. Called on sign-out and whenever the account changes. */
export function clearEditorMemory(): void {
  MEMORY = EMPTY_MEMORY;
}

/**
 * The memory, if it is this account's — and nothing at all if it is not.
 *
 * Dropping rather than merely hiding is deliberate: a design nobody may open is not kept around on
 * the chance that its owner comes back to this tab. The storage copy, which IS theirs, still is.
 */
function memoryFor(ownerKey: string | null): EditorMemory {
  if (MEMORY.ownerKey !== ownerKey) clearEditorMemory();
  return MEMORY;
}

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  const next = reduce(state, action);
  MEMORY = { ...MEMORY, editor: next };
  return next;
}

/**
 * Hand the editor a working copy it did not make — the wizard's, when the draft write failed.
 *
 * Creating a template and storing its design are two requests. When the second one fails the
 * template EXISTS on the server carrying the backend's default draft, and the seven steps' work is
 * only in the tab. Telling the administrator to «save it again in the editor» while the editor
 * reads that default draft recovers nothing: it recovers an empty template.
 *
 * So the wizard puts its layout here first, under the id the SERVER gave, and only then navigates.
 * The editor opens on it as an UNSAVED copy — `dirty`, "Kaydedilmedi" showing — and «Taslak Kaydet»
 * writes it to that same template. It is the same cell that already outlives a visit to the screen,
 * so leaving the editor and coming back does not lose it either.
 */
export function primeEditorDraft(
  templateId: string,
  template: DemoTemplate,
  ownerKey: string | null = null,
): void {
  const recovered = { ...template, id: templateId };
  MEMORY = {
    ownerKey,
    recovered,
    /* The same instant the storage copy is stamped with, so the two always agree. */
    recoveredAt: new Date().toISOString(),
    editor: { ...freshEditorState(recovered), tplId: templateId, dirty: true },
    /* This session is the RECOVERY's; the editor may only resume it when that is what was chosen. */
    editorSource: 'recovered',
    /* A recovery is about the certificate. The wizard's own share arrangement, if it made one, is
       written to the server by `saveCarriedLinkedInCard`, so nothing is carried here. */
    linkedInDraft: {},
    linkedInDraftTplId: null,
  };
  /*
   * …and outside the tab's memory as well.
   *
   * A module variable survives a route change and nothing else, so pressing reload — which is what
   * somebody does when a screen has just failed — lost the design anyway. `writePendingDraft` keeps
   * it in this tab's storage under the SERVER's template id, for this user, until it is safely
   * stored (`lib/pending-intent.ts`).
   */
  writePendingDraft(templateId, ownerKey, recovered);
}

/**
 * The record the recovery belongs to, when the projection has not got it.
 *
 * The template exists on the SERVER — that is the whole premise — but the read that would bring it
 * into the store may be the very request that failed. Without this the editor would answer «Şablon
 * bulunamadı» and the working copy primed above would be unreachable, which is the same data loss
 * wearing a different message.
 */
/**
 * The recovered record, from the tab's memory or — after a reload emptied that — from its storage,
 * with the moment it was made.
 *
 * Read in that order on purpose: the in-memory copy is the live one an author may have been editing
 * since, and the stored copy is the fallback that makes a hard refresh survivable. Both are now
 * checked against the same owner, and both carry a stamp, because choosing between this and the
 * server's draft is a question about which is newer.
 */
const recoveredDraft = (
  id: string | undefined,
  ownerKey: string | null,
): { template: DemoTemplate; at: string | null } | null => {
  if (!id) return null;
  const memory = memoryFor(ownerKey);
  if (memory.recovered?.id === id) return { template: memory.recovered, at: memory.recoveredAt };
  const saved = readPendingDraftRecord<DemoTemplate>(id, ownerKey);
  if (!saved) return null;
  const template = { ...saved.template, id };
  MEMORY = { ...memory, ownerKey, recovered: template, recoveredAt: saved.startedAt };
  return { template, at: saved.startedAt };
};

/** `Screens.editor`'s seeding block (`m08-admin.js:177`): a deep copy of the draft, the first
 *  layer selected, and the test data taken from every field's own sample. */
function freshEditorState(t: DemoTemplate): EditorState {
  const layout = stripDraft(t.draft);
  return {
    ...INITIAL_EDITOR_STATE,
    tplId: t.id,
    layout,
    sel: layout.layers[0]?.id ?? null,
    testData: defaultTestData(t, layout),
    /* The editor opens on the language the draft says it is written in, so an English template
       does not open in Turkish and invite the author to type English into the Turkish boxes. */
    editLocale: layout.defaultLanguage === 'en' ? 'en' : 'tr',
  };
}

// ------------------------------------------------------------------------- readout & fit notice

interface LiveProps {
  live: LiveChannel;
  layout: EditorLayout;
  sel: string | null;
  testData: Record<string, string>;
}

/** `renderReadout` — `m08-admin.js:283`. A text layer reports its punto (as `base → shrunk` once
 *  shrink-to-fit has taken it down), everything else its width in pixels. */
function Readout({ live, layout, sel, testData, scale }: LiveProps & { scale: number }): ReactElement {
  useSyncExternalStore(live.subscribe, live.getSnapshot, live.getSnapshot);
  const l = layerById(layout, sel);
  if (!l) {
    return (
      <span className="readout" id="readout">
        <span>Katman seçilmedi</span>
      </span>
    );
  }
  const r = resolveLayers(layout, previewData(testData)).find((x) => x.layer.id === l.id);
  const W = canvasW(layout.canvas as Canvas);
  const base = sizePx(asRenderLayer(l), layout.canvas as Canvas);
  return (
    <span className="readout" id="readout">
      <span>
        X <b>{`${(l.x * 100).toFixed(1)}%`}</b>
      </span>
      <span>
        Y <b>{`${(l.y * 100).toFixed(1)}%`}</b>
      </span>
      <span>
        Genişlik <b>{`${(l.width * 100).toFixed(1)}%`}</b>
      </span>
      {r && r.kind === 'text' ? (
        <span>
          Punto <b>{r.size === base ? r.size : `${base} → ${r.size}`}</b>
        </span>
      ) : (
        <span>
          Boyut <b>{`${Math.round(l.width * W)} px`}</b>
        </span>
      )}
      {l.rotation ? (
        <span>
          Açı <b>{`${l.rotation}°`}</b>
        </span>
      ) : null}
      <span>
        Zoom <b>{`${Math.round(scale * 100)}%`}</b>
      </span>
    </span>
  );
}

/** `renderFitNotice` — `m08-admin.js:289`. One box for the selected layer, in the reference's own
 *  wording: what a QR really encodes, what an image really resolved to, and for text either the
 *  overflow that a bigger box would fix or the shrink that already happened. */
function FitNotice({ live, layout, sel, testData }: LiveProps): ReactElement {
  useSyncExternalStore(live.subscribe, live.getSnapshot, live.getSnapshot);
  const l = layerById(layout, sel);
  const r = l ? resolveLayers(layout, previewData(testData)).find((x) => x.layer.id === l.id) : undefined;
  if (!l || !r) return <div id="fitNotice" />;

  if (r.kind === 'qr') {
    return (
      <div id="fitNotice">
        {r.matrix ? (
          <div className="ok-box">
            {`QR gerçek: ${r.matrix.length}×${r.matrix.length} modül · hedef ${r.text} · taranabilir.`}
          </div>
        ) : (
          <div className="info-box">QR kütüphanesi yüklenemedi; yer tutucu gösteriliyor.</div>
        )}
      </div>
    );
  }

  if (r.kind === 'image') {
    return (
      <div id="fitNotice">
        {r.asset ? (
          <div className="ok-box">
            {`Görsel katmanı: ${r.asset.originalFileName || r.asset.id} · ${r.asset.width}×${r.asset.height} · `}
            <span className="mono">{`${String(r.asset.sha256).slice(0, 12)}…`}</span>
          </div>
        ) : (
          <div className="warn-box">
            {`Görsel referansı çözülemedi${r.refError ? ` (${r.refError})` : ''}; render fail-closed davranır.`}
          </div>
        )}
      </div>
    );
  }

  const base = sizePx(asRenderLayer(l), layout.canvas as Canvas);
  if (r.overflow) {
    return (
      <div id="fitNotice">
        <div className="warn-box">
          <b>{`«${r.text}»`}</b>
          {` ${l.fitMode === 'shrink' ? `minimum punto ${r.floor} px'te bile` : `${base} px sabit puntoda`} ${Math.round(r.maxW)} px alana sığmıyor (${Math.round(r.width)} px). Genişliği artırın, minimum puntoyu düşürün ya da satır sayısını ${(l.maxLines || 1) + 1} yapın. Otomatik ikinci satıra geçilmez; toplu üretimde bu değer uyarıyla işaretlenir.`}
        </div>
      </div>
    );
  }
  if (r.size < base) {
    return (
      <div id="fitNotice">
        <div className="info-box">
          Uzun metin için punto <b>{`${base} → ${r.size} px`}</b>
          {`'e küçültüldü (shrink-to-fit). Alan: ${Math.round(r.width)} / ${Math.round(r.maxW)} px.`}
        </div>
      </div>
    );
  }
  return (
    <div id="fitNotice">
      <div className="ok-box">
        {`«${r.text}» alana sığıyor · ${r.size} px · ${Math.round(r.width)} / ${Math.round(r.maxW)} px${r.lines.length > 1 ? ` · ${r.lines.length} satır` : ''}.`}
      </div>
    </div>
  );
}

/**
 * `openFontUploadModal` — `m08-admin.js:628`.
 *
 * The editor's own font gate, NOT the library's («Font meta verisi», `AssetsScreen`): a shorter
 * weight list, its own guesses from the file name and its own licence sentence. The two dialogs
 * differ upstream and are kept apart here, because the wrong family name or weight is what breaks
 * a release's font digest contract and the wording is how each surface says so.
 */
function FontUploadModal({
  file,
  onCancel,
  onSave,
}: {
  file: File;
  onCancel: () => void;
  onSave: (meta: { family: string; weight: number; style: string }) => void;
}): ReactElement {
  const toast = useToast();
  const guess =
    file.name
      .replace(/\.[^.]+$/, '')
      .replace(/[-_ ]?(regular|semibold|bold|medium|light|extrabold|italic)+$/i, '')
      .trim() || 'Özel Font';
  const guessedWeight = /extrabold|800/i.test(file.name)
    ? 800
    : /bold|700/i.test(file.name)
      ? 700
      : /semibold|600/i.test(file.name)
        ? 600
        : /medium|500/i.test(file.name)
          ? 500
          : /light|300/i.test(file.name)
            ? 300
            : 400;
  const [family, setFamily] = useState(guess);
  const [weight, setWeight] = useState(String(guessedWeight));
  const [style, setStyle] = useState(/italic/i.test(file.name) ? 'italic' : 'normal');
  const [licensed, setLicensed] = useState(false);

  return (
    <Modal label="Font yükle" onClose={onCancel}>
      <ModalHead title="Font dosyası" onClose={onCancel} />
      <div className="card-pad">
        <div className="file-chip">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <span>{`${file.name} · ${Math.round(file.size / 1024)} KB`}</span>
        </div>
        <div className="field">
          <label htmlFor="fuFamily">Font family adı</label>
          <input className="input" id="fuFamily" value={family} onChange={(e) => setFamily(e.target.value)} />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="fuWeight">Gerçek kalınlık</label>
            <select
              className="select"
              id="fuWeight"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
            >
              {[300, 400, 500, 600, 700, 800].map((w) => (
                <option value={String(w)} key={w}>
                  {String(w)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="fuStyle">Stil</label>
            <select className="select" id="fuStyle" value={style} onChange={(e) => setStyle(e.target.value)}>
              <option value="normal">normal</option>
              <option value="italic">italic</option>
            </select>
          </div>
        </div>
        <label className="check">
          <input
            type="checkbox"
            id="fuLicense"
            checked={licensed}
            onChange={(e) => setLicensed(e.target.checked)}
          />
          {' Bu fontun kullanım lisansına sahip olduğumu onaylıyorum.'}
        </label>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={onCancel}>
          Vazgeç
        </button>
        <button
          className="btn btn-primary"
          id="fuGo"
          type="button"
          onClick={() => {
            /* The licence is a gate, not a formality: the reference refuses and says so. */
            if (!licensed) {
              toast('Lisans onayı gerekli');
              return;
            }
            onSave({ family: family.trim() || guess, weight: Number.parseInt(weight, 10), style });
          }}
        >
          Fontu kaydet
        </button>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------ the screen

export function EditorScreen(): ReactElement {
  /* The demo's route table is not this file's to write, and the extra screens spell the parameter
     `:templateId`; the reference's own path is `#/admin/templates/{id}/editor`. Both spellings are
     accepted so the screen works under either. */
  const params = useParams();
  const id = params.templateId ?? params.id;
  /* The projection first; the recovered record only where the projection has nothing under that id. */
  const ownerKey = useV07Account()?.ownerKey ?? null;
  const fromServer = useTemplate(id);
  /*
   * Which copy the editor opens, when there are two — decided by WHEN, not by emptiness.
   *
   * Two wrong rules preceded this one. «The projection, and the recovery only if the projection has
   * nothing» lost the recovery entirely, because the template really is in the projection after its
   * own POST succeeded — carrying the backend's default draft. Replacing it with «the recovery wins
   * whenever the server's draft has no layers» swapped one guess for another: a draft with zero
   * layers is not proof that nobody saved it. `updateDraft` accepts `layers: []`, so an author can
   * clear a design, set a background, change a field or a policy and save — and a stale recovery
   * would then silently overwrite work that was done AFTER it.
   *
   * `DemoTemplate.updatedAt` is the server draft's own `updated_at`, and the recovery carries the
   * instant it was made. That is a real comparison rather than an inference:
   *
   *   - server at least as new as the recovery  → the server, and the recovery is provably older;
   *   - recovery newer, server draft untouched  → the recovery, which is the only design there is;
   *   - recovery newer, server draft has a drawing → nobody can safely choose. Both are kept and
   *     the author is asked, because the alternative is deleting somebody's work to avoid a
   *     question.
   *
   * When a stamp is missing or unreadable the same caution applies: a server draft carrying a
   * drawing is a conflict rather than a winner.
   */
  const recovery = recoveredDraft(id, ownerKey);
  const serverAt = Date.parse(fromServer?.updatedAt ?? '');
  const recoveredAt = Date.parse(recovery?.at ?? '');
  const comparable = Number.isFinite(serverAt) && Number.isFinite(recoveredAt);
  const serverIsNewer = comparable && serverAt >= recoveredAt;
  const serverHasDesign = (fromServer?.draft?.layers?.length ?? 0) > 0;
  const conflict = recovery !== null && !serverIsNewer && serverHasDesign && fromServer !== null;
  const [resolved, setResolved] = useState<'server' | 'recovered' | null>(null);

  /*
   * The choice, named — because it has to travel further than this line.
   *
   * The editor caches its working session per template, and that cache was consulted on the id
   * alone; a session about the OTHER copy would then be resumed over whatever was chosen here. So
   * the decision is a value that goes down with the template rather than an implication of it.
   */
  const source: DraftSourceChoice =
    recovery === null || serverIsNewer
      ? 'server'
      : conflict && resolved !== 'recovered'
        ? 'server'
        : 'recovered';
  const template = source === 'server' ? fromServer : (recovery?.template ?? fromServer);

  /* `Screens.editor`'s own first branch (`m08-admin.js:176`): the head says so and nothing else
     is drawn — no stage, no property panel. */
  if (!template) {
    return (
      <section data-screen="editor">
        <div className="page-head">
          <div>
            <Link
              className="small"
              to="/admin/templates"
              style={{ color: 'var(--text-3)', textDecoration: 'none' }}
            >
              ← Şablonlar
            </Link>
            <h1 id="editorTitle">Şablon bulunamadı</h1>
            <p className="lead" id="editorLead" />
          </div>
        </div>
        <div className="editor">
          <div className="stage-wrap">
            <div className="stage-outer">
              <div className="stage-viewport" id="stageViewport">
                <div className="stage" id="stage" />
              </div>
            </div>
          </div>
          <aside className="card props" id="props" />
        </div>
      </section>
    );
  }

  /*
   * The one case nothing may decide on the author's behalf.
   *
   * Both designs are real, both are this account's, and the recovery is the newer of the two — so
   * whichever is picked silently, something somebody made is gone. The screen therefore stops here
   * and shows what each one is, and choosing «sunucudaki» does NOT delete the recovery: it stays in
   * storage until a save replaces it, so the choice is reversible.
   */
  if (conflict && resolved === null) {
    return (
      <section data-screen="editor">
        <div className="page-head">
          <div>
            <Link
              className="small"
              to="/admin/templates"
              style={{ color: 'var(--text-3)', textDecoration: 'none' }}
            >
              ← Şablonlar
            </Link>
            <h1 id="editorTitle">İki tasarım var</h1>
            <p className="lead" id="editorLead">
              {
                'Bu şablonun sunucudaki taslağı ile bu tarayıcıda kurtarılan tasarım aynı değil ve kurtarılan kopya daha yeni. Hangisiyle devam edeceğinizi seçin; seçmediğiniz kopya silinmez.'
              }
            </p>
          </div>
        </div>
        <div className="card card-pad stack" id="editorConflict">
          <div className="warn-box">
            {
              'Otomatik seçim yapılmadı: ikisinden birini kendiliğinden açmak, diğerinin üzerine yazma riski taşır.'
            }
          </div>
          <div className="grid-2">
            <div className="card card-pad stack" style={{ gap: 8 }}>
              <b>Sunucudaki taslak</b>
              <span className="small muted">
                {`${fromServer?.draft?.layers?.length ?? 0} katman · son kayıt ${
                  Number.isFinite(serverAt) ? fmtDateTime(fromServer?.updatedAt ?? '', 'tr') : 'bilinmiyor'
                }`}
              </span>
              <button
                className="btn btn-secondary"
                type="button"
                id="edConflictServer"
                onClick={() => setResolved('server')}
              >
                Sunucudaki taslağı aç
              </button>
            </div>
            <div className="card card-pad stack" style={{ gap: 8 }}>
              <b>Kurtarılan tasarım</b>
              <span className="small muted">
                {`${recovery?.template.draft?.layers?.length ?? 0} katman · ${
                  Number.isFinite(recoveredAt) ? fmtDateTime(recovery?.at ?? '', 'tr') : 'zamanı bilinmiyor'
                } · kaydedilmedi`}
              </span>
              <button
                className="btn btn-primary"
                type="button"
                id="edConflictRecovered"
                onClick={() => setResolved('recovered')}
              >
                Kurtarılan tasarımı aç
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /*
   * The key carries the ACCOUNT as well as the template.
   *
   * `key={template.id}` alone meant that signing out and in as somebody else, on the same template,
   * re-used the mounted editor and therefore its reducer state — the previous person's unsaved
   * work, still on screen under the new session.
   */
  return (
    <TemplateEditor
      template={template}
      source={source}
      key={`${ownerKey ?? 'anon'}:${template.id}:${source}`}
    />
  );
}

function TemplateEditor({
  template,
  source,
}: {
  template: DemoTemplate;
  /** Which copy the screen chose, so a cached session about the other one is not resumed over it. */
  source: DraftSourceChoice;
}): ReactElement {
  /* Whose editing session this is: the memory cell is claimed for them and dropped for anyone else. */
  const ownerKey = useV07Account()?.ownerKey ?? null;
  const db = useDemoData();
  const queries = useV07Queries();
  /*
   * The per-template credential counts, from the server.
   *
   * This screen prints «N kayıt» beside every template and can sort by it, and the editor's publish
   * gate asks the same question before it says a release may still change. Counting the store would
   * answer with whatever page of the register happened to be loaded.
   */
  const countsRead = useRead('templates:counts', () => queries.registerSummary());
  useAdoptTemplateCounts(countsRead.value?.byTemplate ?? null);
  const commands = useV07Commands();
  const toast = useToast();
  const navigate = useNavigate();

  /*
   * The working copy, when it is this account's and about this template.
   *
   * `memoryFor` is what makes the second condition real: picking the cache on `tplId` alone meant
   * that the reducer's starting state could be the previous account's editing session even after
   * the selection above had correctly chosen the server's copy — one door closed, the next one open.
   */
  const [state, dispatch] = useReducer(editorReducer, template, (t) => {
    const memory = memoryFor(ownerKey);
    /*
     * A cached session is resumed only when it is this account's, about this template, AND about the
     * copy the screen just chose. The third condition is the one that was missing: the selection
     * above could pick the server's draft while this line handed back the recovery's editing
     * session, so the choice had no effect and «Taslak Kaydet» would have written the wrong design.
     */
    const resumable = memory.editor.tplId === t.id && memory.editor.layout && memory.editorSource === source;
    if (resumable) return memory.editor;
    const fresh = freshEditorState(t);
    MEMORY = { ...memory, ownerKey, editor: fresh, editorSource: source };
    return fresh;
  });
  const [live] = useState(createLiveChannel);
  const [outerWidth, setOuterWidth] = useState(0);
  const [renderRun, setRenderRun] = useState(0);
  const [dialog, setDialog] = useState<'preview' | 'publish' | 'archive' | 'email' | 'linkedinCard' | null>(
    null,
  );
  const templateEmail = useTemplateEmail();
  /* «LinkedIn görseli»: the share image, arranged after the certificate exists. */
  const linkedInCard = useTemplateLinkedInCard();
  /*
   * The share arrangement the author has not saved yet, kept HERE rather than in the dialog.
   *
   * The dialog unmounts the moment it is closed, so anything it owned would go with it — and
   * «I closed the panel to look at the certificate» is not a decision to throw away a layout.
   * Holding it on the editor is the same remedy the reference uses for the certificate's own
   * unsaved copy: leaving and coming back finds the work where it was left.
   */
  const [linkedInDraft, rememberLinkedInDraft] = useState<LinkedInCardDrafts>(() => {
    const memory = memoryFor(ownerKey);
    return memory.linkedInDraftTplId === template.id ? memory.linkedInDraft : {};
  });
  /*
   * One setter, two places: the screen's state and the cell that outlives the screen.
   *
   * The panel reports `null` when the arrangement has actually been saved or the author chose the
   * automatic card again, so the same call that clears the dot clears the memory — there is no
   * separate «forget» step to leave out.
   */
  const setLinkedInDraft = useCallback(
    (locale: 'tr' | 'en', next: LinkedInCardDesign | null) => {
      /* One language's key, never the other's: a save in English leaves unsaved Turkish work alone. */
      rememberLinkedInDraft((current) => {
        const bag: LinkedInCardDrafts = { ...current };
        if (next === null) delete bag[locale];
        else bag[locale] = next;
        MEMORY = {
          ...memoryFor(ownerKey),
          ownerKey,
          linkedInDraft: bag,
          linkedInDraftTplId: Object.keys(bag).length === 0 ? null : template.id,
        };
        return bag;
      });
    },
    [ownerKey, template.id],
  );
  /** Any language with unsaved work — what the head's dot and the unload warning are about. */
  const linkedInDirty = Object.keys(linkedInDraft).length > 0;
  /*
   * «Önizlemeleri incele» — the publish gate's own blocking row, and a step of THIS editing session.
   *
   * `__previewChecked` is set in exactly one place in the frozen artifact: the demo seed's
   * `publishSeedRelease`. Nothing in the reference's editor sets it, so for a template the PRODUCT
   * creates the row stays ✕ and «Release'i yayınla» can never enable — while the row's own detail
   * tells the administrator to run a step that does not exist. No template made in this product
   * could be published at all; every published release came from a seed.
   *
   * It lives here rather than in the store because the store is a projection of the server: the
   * silent save that «Yayınla» performs before opening the gate re-reads it, and a flag written with
   * `patchLocal` would be wiped on the way to the very dialog that reads it. It decides nothing on
   * its own — the release is cut by `template.publish_release`, which runs its own gate server-side.
   */
  const [previewReviewed, setPreviewReviewed] = useState(false);
  /*
   * The libraries, READ — the same read the library screen uses.
   *
   * They were two component states seeded from a dump, so a background uploaded here existed only
   * until the tab was refreshed and was never in the library screen's grid at all. Now the editor's
   * picker, its family select and the library screen all answer from one read: the demo's own library,
   * or the backend's with the bytes it is allowed to read.
   */
  const library = useRead('asset-library', () => queries.assetLibrary());
  const assets = library.value?.assets ?? [];
  const fonts = library.value?.fonts ?? [];
  /*
   * The read's OUTCOME travels with its arrays.
   *
   * Without it the panel cannot tell «boş» from «okunamadı» — an empty array is what both look like —
   * and there is no way to ask again short of reloading the page, which throws away the unsaved
   * layout this screen exists to hold. `retry` is a state bump inside the hook, so the working copy,
   * the undo stack and the «Kaydedilmedi» pill all survive it.
   */
  const libraryState = useMemo<LibraryReadState>(
    () => ({ status: library.status, stale: library.stale, error: library.error, retry: library.retry }),
    [library.status, library.stale, library.error, library.retry],
  );
  const [fontFile, setFontFile] = useState<{ file: File; layer: EditorLayer | null } | null>(null);

  const outerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const propsRef = useRef<HTMLElement>(null);
  const unsavedRef = useRef<HTMLSpanElement>(null);

  /* `renderStage` measures `outer.clientWidth` (`m08-admin.js:234`), so the width has to be read
     from the laid-out DOM rather than assumed. Upstream re-measures on `resize`, debounced 120ms;
     this does the same, in a layout effect so the first measured pass is the first painted one. */
  useLayoutEffect(() => {
    const outer = outerRef.current;
    if (outer === null) return;
    const measure = (): void => setOuterWidth(outer.clientWidth);
    measure();
    let timer: number | undefined;
    const onResize = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, 120);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const layout = state.layout;
  const fontRows = useMemo(() => fontReadiness(db.templates, fonts), [db.templates, fonts]);
  /* `FontState.ready` — `m01-utils.js:159`. */
  const fontsReady = fontRows.length > 0 && fontRows.every((r) => r.status === 'loaded');

  /* `markDirty` — `m08-admin.js:207`. The drag path does not dispatch, so the pill is unhidden the
     way the reference unhides it and the reducer is told at pointerup. */
  const markDirtyPill = useCallback((): void => {
    if (unsavedRef.current) unsavedRef.current.hidden = false;
  }, []);

  /* `syncPropsFromLayer` — `m08-admin.js:627`. The four geometry inputs are written straight into
     the property panel during a drag, skipping the input the operator is typing in. */
  const syncPropsFromLayer = useCallback((l: EditorLayer): void => {
    const panel = propsRef.current;
    if (panel === null) return;
    const set = (k: string, v: string | number): void => {
      const i = panel.querySelector<HTMLInputElement>(`[data-l="${k}"]`);
      if (i && document.activeElement !== i) i.value = String(v);
    };
    set('x', (l.x * 100).toFixed(1));
    set('y', (l.y * 100).toFixed(1));
    set('width', (l.width * 100).toFixed(1));
    set('rotation', l.rotation || 0);
  }, []);

  /* `mut` — `bindLayerProps`, `m08-admin.js:349`: snapshot, mutate the working layout IN PLACE (so
     a caller holding one of its layers mutates the live object, as upstream's closures do), then
     tell the reducer. The mutation is deliberately outside the reducer, which stays pure. */
  const mutate = useCallback(
    (fn: (working: EditorLayout) => void): void => {
      if (layout === null) return;
      const before = structuredClone(layout);
      fn(layout);
      dispatch({ type: 'mutated', before });
    },
    [layout],
  );

  const doUndo = useCallback((): void => {
    if (!state.undo.length) {
      toast('Geri alınacak adım yok');
      return;
    }
    dispatch({ type: 'undo' });
  }, [state.undo.length, toast]);

  const doRedo = useCallback((): void => {
    if (!state.redo.length) {
      toast('İleri alınacak adım yok');
      return;
    }
    dispatch({ type: 'redo' });
  }, [state.redo.length, toast]);

  /*
   * `saveDraft` — `m08-admin.js:208`. The ONLY write back to the template record.
   *
   * It answers whether the write landed, because one caller has to know: «Yayınla» saves silently
   * first so the gate publishes what the record holds, and the server publishes THE STORED DRAFT. If
   * that silent save failed and the gate opened anyway, the checklist would be run against what is on
   * screen while the release was cut from what the server still had — a publication that looks like
   * the administrator's design and is not it.
   */
  const saveDraft = useCallback(
    async (silent?: boolean): Promise<boolean> => {
      if (layout === null) return false;
      const draft = stripDraft(layout) as unknown as DemoDraft;
      const r = await commands.saveDraft({
        templateId: template.id,
        draft,
        canonicalTitle: template.name,
      });
      if (!r.ok) {
        /* A failed save must not clear the dirty flag: the unsaved copy is still the only copy. */
        toast(r.message ?? 'Taslak kaydedilemedi');
        return false;
      }
      dispatch({ type: 'saved' });
      /*
       * The safe point, and the only one.
       *
       * The recovery copy is dropped HERE and nowhere earlier: the server has acknowledged the
       * design, so keeping an older copy in storage could only serve to overwrite a newer one on
       * some later reload. A failed save returns above without touching it, because then the
       * unsaved copy is still the only copy there is.
       */
      clearPendingDraft(template.id);
      if (MEMORY.recovered?.id === template.id) MEMORY = { ...MEMORY, recovered: null, recoveredAt: null };
      if (!silent) toast('Taslak kaydedildi — yayınlanan release değişmedi');
      return true;
    },
    [commands, layout, template.id, template.name, toast],
  );

  /* `beforeunload` — `m08-admin.js:204`. The reference guards the tab, not the in-app navigation,
     and returning to the same template keeps the unsaved copy, so this is the whole guard. */
  useEffect(() => {
    /* An unsaved LinkedIn arrangement is unsaved work too, and a reload ends it just as finally. */
    if (!state.dirty && !linkedInDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [state.dirty, linkedInDirty]);

  // ------------------------------------------------------------------------------ the fit calc

  const canvas = (layout?.canvas ?? { width: 1, height: 1 }) as Canvas;
  const W = canvasW(canvas);
  const H = canvasH(canvas);
  /* `renderStage` — `m08-admin.js:233`. 38px is the stage frame's own padding plus its border, 760
     caps the width a template may claim of the column and 620 the height of the viewport; 720 is
     the point past which the viewport scrolls instead of growing. */
  const avail = Math.max(240, outerWidth - 38);
  const fitScale = Math.min(Math.min(avail, 760) / W, 620 / H);
  const scale = state.zoom === 'fit' ? fitScale : state.zoom;
  const dispW = Math.round(W * scale);
  const dispH = Math.round(H * scale);
  const borderWidth = Math.max(2, Math.round(2 / scale));

  /*
   * THE STAGE DRAWS THE LANGUAGE BEING EDITED.
   *
   * `staged` is the same design as one language is sealed in — that language's literals on that
   * language's artwork (`localizedEditorLayout`) — and it is what the drawing, the layer boxes and
   * the shrink-to-fit readout are computed from, so switching the language shows the real
   * certificate rather than a relabelled one. The declared language returns the layout itself, so
   * a single-language template is drawn by exactly the code that drew it before.
   *
   * It is a VIEW: every edit still goes to `layout`, which is what gets saved.
   */
  const staged = layout === null ? null : localizedEditorLayout(layout, state.editLocale);
  const data = previewData(state.testData, layout === null ? undefined : { lang: state.editLocale, layout });
  /* Resolved on every render, as `renderStage` resolves on every call: the working layout is
     mutated in place, so a memo keyed on its identity would hand back stale geometry. */
  const resolved = staged ? resolveLayers(staged, data) : [];

  const selectedLayer = layerById(layout, state.sel);
  /* `renderProps`'s memo key — `m08-admin.js:302`. React's reconciliation is what actually keeps
     the panel's DOM (and its focus) across a value-only change, so the key is carried for the
     contract and for a reader comparing this screen with `ED`, not consulted. */
  const propsKey = layout
    ? `${template.id}|${state.editLocale}|${state.tab}|${state.sel}|${layout.layers.length}|${(layout.fieldDefinitions || []).length}|${(layout.safeAreas || []).length}|${[...new Set(fonts.map((f) => f.family))].join(',')}|${selectedLayer ? `${selectedLayer.font}${selectedLayer.align}${selectedLayer.kind}${selectedLayer.fitMode}` : ''}|${template.status}|${W}x${H}`
    : '';

  useEffect(() => {
    if (state.scale !== scale || state.propsKey !== propsKey) {
      dispatch({ type: 'derived', derived: { scale, propsKey } });
    }
  }, [scale, propsKey, state.scale, state.propsKey]);

  // ------------------------------------------------------------------------------- interaction

  /* `updateStageLive` — `m08-admin.js:253`. The boxes and the background are moved where they
     stand; nothing is rebuilt and the reducer is not told until the pointer comes up. */
  const updateStageLive = useCallback((): void => {
    if (layout === null) return;
    const liveLayout = localizedEditorLayout(layout, state.editLocale);
    const liveData = previewData(state.testData, { lang: state.editLocale, layout });
    const res = resolveLayers(liveLayout, liveData);
    if (bgRef.current) bgRef.current.innerHTML = previewSVG(asRenderLayout(liveLayout), liveData);
    const stage = stageRef.current;
    if (stage) {
      for (const r of res) {
        const box = stage.querySelector<HTMLElement>(`.layer-box[data-layer="${r.layer.id}"]`);
        if (!box) continue;
        const g = editorBoxGeometry(layout, r);
        Object.assign(box.style, {
          left: `${g.left}px`,
          top: `${g.top}px`,
          width: `${g.w}px`,
          height: `${g.h}px`,
          transform: r.layer.rotation ? `rotate(${r.layer.rotation}deg)` : '',
        });
        box.classList.toggle('is-overflow', r.kind === 'text' && r.overflow);
      }
    }
    live.bump();
    /* `editLocale` is read here, so it belongs in the list: the live redraw has to follow the
       language being edited, or a drag would repaint the stage in the other language's words. */
  }, [layout, state.testData, state.editLocale, live]);

  /* `startDrag` — `m08-admin.js:265`. One handler for three gestures: the box moves, the side
     handle resizes (doubled for a centred or square layer, because both edges move), and the round
     handle rotates about the box's centre, snapping to 15° with Shift. */
  const startDrag = useCallback(
    (box: HTMLElement, l: EditorLayer, e: PointerEvent): void => {
      if (layout === null) return;
      const target = e.target as HTMLElement;
      const isRot = target.classList.contains('rot');
      const isHandle = !isRot && target.classList.contains('handle');
      const side = target.classList.contains('left') ? -1 : 1;
      const rect = box.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const start = {
        x: e.clientX,
        y: e.clientY,
        lx: l.x,
        ly: l.y,
        lw: l.width,
        lr: l.rotation || 0,
        a0: Math.atan2(e.clientY - cy, e.clientX - cx),
      };
      dispatch({ type: 'push_undo', before: structuredClone(layout) });
      box.classList.add('is-dragging');
      try {
        box.setPointerCapture(e.pointerId);
      } catch {
        /* Pointer capture is a convenience; a browser that refuses it still tracks the move. */
      }
      const move = (ev: PointerEvent): void => {
        if (isRot) {
          const a = Math.atan2(ev.clientY - cy, ev.clientX - cx);
          let deg = start.lr + ((a - start.a0) * 180) / Math.PI;
          if (ev.shiftKey) deg = Math.round(deg / 15) * 15;
          l.rotation = Math.round(deg * 10) / 10;
        } else {
          const dx = (ev.clientX - start.x) / scale / W;
          const dy = (ev.clientY - start.y) / scale / H;
          if (isHandle) {
            const d = dx * side;
            l.width = clamp(
              start.lw + (l.kind === 'qr' || l.kind === 'image' || l.align === 'center' ? 2 * d : d),
              0.02,
              1.5,
            );
          } else {
            l.x = clamp(start.lx + dx, 0, 1);
            l.y = clamp(start.ly + dy, 0, 1);
            if (state.snap) snapLayer(l);
          }
        }
        markDirtyPill();
        updateStageLive();
        syncPropsFromLayer(l);
      };
      const up = (): void => {
        box.removeEventListener('pointermove', move);
        box.removeEventListener('pointerup', up);
        box.removeEventListener('pointercancel', up);
        box.classList.remove('is-dragging');
        dispatch({ type: 'commit' });
      };
      box.addEventListener('pointermove', move);
      box.addEventListener('pointerup', up);
      box.addEventListener('pointercancel', up);
    },
    [H, W, layout, markDirtyPill, scale, state.snap, syncPropsFromLayer, updateStageLive],
  );

  /* `stagePointerDown` — `m08-admin.js:259`. A press on an unselected layer selects it AND starts
     the drag in the same gesture. Upstream has to look the box up again after re-rendering the
     stage; React keeps the element (the boxes are keyed by layer id), so the one under the pointer
     is still the right one. */
  const onStagePointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const box = (e.target as HTMLElement).closest<HTMLElement>('.layer-box');
    if (!box) return;
    e.preventDefault();
    const id = box.dataset.layer ?? null;
    const l = layerById(layout, id);
    if (!l) return;
    if (state.sel !== id) dispatch({ type: 'select', id });
    if (!l.locked) startDrag(box, l, e.nativeEvent);
  };

  /* The `#stageViewport` keydown — `m08-admin.js:194`: undo/redo, and an arrow-key nudge of 0.25%
     (1% with Shift) that respects the snap toggle and the layer's lock. */
  const onViewportKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) doRedo();
      else doUndo();
      return;
    }
    const l = layerById(layout, state.sel);
    if (!l || l.locked) return;
    const step = e.shiftKey ? NUDGE_STEP_SHIFT : NUDGE_STEP;
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const k = delta[e.key];
    if (!k) return;
    e.preventDefault();
    mutate(() => {
      l.x = clamp(l.x + k[0], 0, 1);
      l.y = clamp(l.y + k[1], 0, 1);
      if (state.snap) snapLayer(l);
    });
  };

  /* The panel's live `input` path — upstream's own `input` listeners (`m08-admin.js:373`), which
     mark the draft dirty and redraw the stage WITHOUT recording an undo step per keystroke. The
     layout's identity still moves, or the panel's controlled inputs would not see the keystroke. */
  const liveEdit = useCallback(
    (fn: (working: EditorLayout) => void): void => {
      if (layout === null) return;
      fn(layout);
      dispatch({ type: 'commit' });
    },
    [layout],
  );

  // ----------------------------------------------------------------------------- page actions

  const onBgUpload = async (file: File): Promise<void> => {
    /*
     * The same upload command the library screen uses, so an image added here is in the library and an
     * image added there is in this picker. The reference's id and digest are the SERVER's: it sanitises
     * an SVG and strips a raster's chunks before taking the digest, so a digest computed here would
     * name bytes the server never stored and the drawing would resolve to nothing after a reload.
     */
    const r = await commands.uploadAsset({ file });
    const a = r.asset;
    if (!r.ok || !a) {
      toast(r.message ?? 'Görsel kabul edilmedi');
      return;
    }
    /*
     * WHICH document this picture goes on is the working language's answer, not a fixed one.
     *
     * One design, one geometry, one picture PER LANGUAGE — the same split the server seals with
     * (`domain/issuance/locale.ts`) and the same one the library picker beside the stage has always
     * written. The declared language's artwork is the release's own `background`; any other language
     * owns `localizedBackground[locale]` alone. Read the language once, outside the mutation, so a
     * switch made while the upload was in flight cannot land the picture on the wrong document.
     */
    const locale = state.editLocale;
    /* What the mutation really did, so the message cannot claim a document that was not written. */
    const applied: { to: 'declared' | 'locale' | null } = { to: null };
    mutate((working) => {
      if (editsDeclaredLanguage(working, locale)) {
        working.background = makeBackground({
          assetRef: { assetId: a.id, sha256: a.sha256 },
          hasAlpha: a.hasAlpha,
          fitMode: 'contain',
        }) as unknown as EditorLayout['background'];
        applied.to = 'declared';
        return;
      }
      setLocaleBackground(working, locale, {
        assetRef: { assetId: a.id, sha256: a.sha256 },
        ...(a.hasAlpha === undefined ? {} : { hasAlpha: a.hasAlpha }),
      });
      applied.to = 'locale';
    });
    if (applied.to === null) {
      /* `mutate` does nothing without an open draft; the picture is in the library either way. */
      toast('Görsel kütüphaneye eklendi; arka plan uygulanamadı — taslak açık değil');
      return;
    }
    toast(
      `${applied.to === 'declared' ? 'Arka plan yüklendi' : `Arka plan yüklendi · yalnız ${localeLabel(locale)} belgesi`} · ${a.width}×${a.height} · ${String(a.sha256).slice(0, 10)}…${r.deduped ? ' · aynı içerik zaten kütüphanedeydi' : ''}`,
    );
  };

  const saveFont = async (meta: { family: string; weight: number; style: string }): Promise<void> => {
    const pending = fontFile;
    if (!pending) return;
    /*
     * A font is a content-addressed immutable asset too, and the same four values travel with it from
     * both entry points: family, weight, style and the licence acknowledgement the server requires.
     * The face is then loaded from the bytes the server answered with, so the stage measures and draws
     * with the stored face — and if it could not be loaded the toast says so rather than letting a
     * substituted typeface pass for the uploaded one.
     */
    const r = await commands.uploadFont({
      file: pending.file,
      meta: { ...meta, licenseAcknowledged: true },
    });
    const f = r.font;
    if (!r.ok || !f) {
      toast(`Font yüklenemedi: ${r.message ?? 'yükleme reddedildi'}`);
      return;
    }
    const l = pending.layer;
    if (l && (l.kind === 'text' || l.kind === 'static')) {
      l.font = f.family;
      l.weight = f.weight;
      l.style = f.style;
    }
    /* Upstream marks the draft dirty here without pushing an undo step. */
    dispatch({ type: 'commit' });
    setFontFile(null);
    toast(
      `Font ${r.deduped ? 'zaten kayıtlıydı' : 'kaydedildi'}: ${f.family} ${f.weight} ${f.style} · ${String(f.sha256).slice(0, 12)}…${
        f.state === 'loaded' ? '' : ' · yüzü bu oturumda yüklenemedi'
      }`,
    );
  };

  const duplicate = async (): Promise<void> => {
    /* The copy is cut from the STORED draft, so an unsaved change has to land first — or the copy
       is of the record rather than of what the administrator is looking at. */
    if (state.dirty && !(await saveDraft(true))) return;
    const copy = duplicateTemplateRecord(template, new Set(db.templates.map((x) => x.code)));
    copy.userModified = true;
    const r = await commands.duplicateTemplate({ templateId: template.id, copy });
    if (!r.ok) {
      toast(r.message ?? 'Kopya oluşturulamadı');
      return;
    }
    toast('Kopya oluşturuldu');
    void navigate(`/admin/templates/${r.message ?? copy.id}/editor`);
  };

  /* `toggleArchive` — `m08-admin.js:99`. Leaving the archive needs no confirmation and restores
     what the template was; entering it asks first, and says how many records stay published. */
  const setArchived = async (archived: boolean): Promise<void> => {
    const r = await commands.setTemplateArchived({ templateId: template.id, archived });
    toast(
      r.ok
        ? archived
          ? 'Şablon arşivlendi'
          : 'Şablon arşivden çıkarıldı'
        : (r.message ?? 'Arşiv durumu değiştirilemedi'),
    );
  };

  if (layout === null) return <section data-screen="editor" />;

  // ---------------------------------------------------------------------------------- the head

  const release = currentRelease(template);
  const hasCreds = tplCount(db, template.id) > 0;
  /* `typeLabel` — `m02-mockdb.js:217`, read off the WORKING layout rather than the published
     release, because the editor is showing what the draft says. */
  const typeLabel =
    layout.credentialType === 'custom' && layout.customTypeLabel
      ? layout.customTypeLabel
      : typeRec(layout.credentialType).tr;
  const publishedPart = template.releases.length
    ? `yayında v${release?.releaseNumber}, ${hasCreds ? `${fmtNum(tplCount(db, template.id))} kayıt kendi release snapshot'ına sabitlenmiş` : 'henüz kayıt üretilmedi'}`
    : 'henüz yayınlanmadı';
  const lead = `${typeLabel} · ${layout.subjectType === 'organization' ? 'şirket' : 'kişi'} · ${W}×${H} px (${layout.canvas.orientation}) · ${layout.layers.length} katman · ${(layout.fieldDefinitions || []).length} alan · ${publishedPart}`;

  /* `pillTemplate` — `m08-admin.js:8`. */
  const statusPill =
    template.status === 'published'
      ? 'pill-active'
      : template.status === 'draft'
        ? 'pill-info'
        : 'pill-neutral';
  const statusText = `${TEMPLATE_STATUS_TR[template.status]}${template.status === 'published' && release ? ` · v${release.releaseNumber}` : ''}`;

  const holderKey = holderFieldKey(asRenderLayout(layout));
  const chips = layout.subjectType === 'organization' ? TEST_ORGS : TEST_NAMES;
  /* The test-data strip lists every field a layer binds, in layer order, once each. */
  const usedFields = [...new Set(layout.layers.filter((l) => l.fieldKey).map((l) => l.fieldKey as string))];

  // --------------------------------------------------------------------------------- the stage

  const gridLines: ReactElement[] = [];
  if (state.grid) {
    /* The reference's own accumulation, float drift included — the line at 0.15000000000000002 is
       the one it draws, and rounding the step would move every line after the third. */
    for (let i = 0.05; i < 1; i += 0.05) {
      gridLines.push(
        <line key={`v${i}`} x1={(i * W).toFixed(1)} y1="0" x2={(i * W).toFixed(1)} y2={H} />,
        <line key={`h${i}`} x1="0" y1={(i * H).toFixed(1)} x2={W} y2={(i * H).toFixed(1)} />,
      );
    }
  }
  const safeAreas = layout.safeAreas || [];

  return (
    <section data-screen="editor">
      <div className="page-head">
        <div>
          <Link
            className="small"
            to="/admin/templates"
            style={{ color: 'var(--text-3)', textDecoration: 'none' }}
          >
            ← Şablonlar
          </Link>
          <h1 id="editorTitle">
            {`${template.name} `}
            <span className={`pill ${statusPill} none`}>{statusText}</span>{' '}
            <span className="pill pill-info none" id="unsavedPill" ref={unsavedRef} hidden={!state.dirty}>
              Kaydedilmedi
            </span>
          </h1>
          <p className="lead" id="editorLead">
            {lead}
          </p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-ghost"
            type="button"
            id="undoBtn"
            title="Geri al (Ctrl+Z)"
            data-b_undo="1"
            onClick={doUndo}
          >
            ↶ Geri
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            id="redoBtn"
            title="İleri al (Ctrl+Shift+Z)"
            data-b_redo="1"
            onClick={doRedo}
          >
            ↷ İleri
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            id="dupTplBtn"
            data-b_dup="1"
            onClick={() => void duplicate()}
          >
            Kopyasını Oluştur
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            id="archiveTplBtn"
            data-b_arc="1"
            onClick={() => {
              if (template.status === 'archived') void setArchived(false);
              else setDialog('archive');
            }}
          >
            {template.status === 'archived' ? 'Arşivden çıkar' : 'Arşivle'}
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            id="renderPreviewBtn"
            data-b_render="1"
            onClick={() => setRenderRun((n) => n + 1)}
          >
            Sunucu render
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            id="previewTplBtn"
            data-b_prev="1"
            onClick={() => setDialog('preview')}
          >
            Önizle
          </button>
          {/*
           * «Bu sertifikayla gönderilecek e-posta», beside the certificate's own actions.
           *
           * In the head rather than at the foot or behind a menu: it is one of the two things a
           * certificate carries, and an author preparing a send has to be able to see that it
           * exists. It opens the same panel the preparation wizard's step renders, over the same
           * content.
           */}
          <button
            className="btn btn-secondary"
            type="button"
            id="emailTplBtn"
            data-b_email="1"
            data-testid="editor-open-email"
            onClick={() => setDialog('email')}
          >
            E-posta
          </button>
          {/*
           * «LinkedIn görseli», beside the e-mail and for the same reason: it is the second thing
           * a certificate carries that is not the certificate, and an author has to be able to see
           * that it exists. It opens the same panel the preparation wizard's step renders, over
           * the same arrangement. It does not touch the designer above it.
           */}
          <button
            className="btn btn-secondary"
            type="button"
            id="linkedinCardBtn"
            data-b_linkedin_card="1"
            data-testid="editor-open-linkedin-card"
            onClick={() => setDialog('linkedinCard')}
          >
            {linkedInDirty ? 'LinkedIn görseli •' : 'LinkedIn görseli'}
          </button>
          {/*
           * The WORKING LANGUAGE, in the head where it cannot be missed.
           *
           * One template and one design: this pair does not create a second template and does not
           * make an edit. It says which language's words and which language's artwork the panel's
           * boxes and the stage below are showing, and the FILLED one is the answer to «which
           * language am I editing» at every moment — `btn-primary` and `btn-ghost` are the
           * reference's own two button shapes, so the difference is unmistakable without this
           * product inventing a style for it. Both are always drawn, so a template that seals one
           * language still shows plainly which one that is.
           */}
          <button
            className={`btn ${state.editLocale === 'tr' ? 'btn-primary' : 'btn-ghost'}`}
            type="button"
            id="edLangTr"
            data-b_lang="tr"
            data-testid="editor-lang-tr"
            title="Düzenlenen dil: Türkçe"
            aria-pressed={state.editLocale === 'tr'}
            onClick={() => dispatch({ type: 'edit_locale', locale: 'tr' })}
          >
            Türkçe
          </button>
          <button
            className={`btn ${state.editLocale === 'en' ? 'btn-primary' : 'btn-ghost'}`}
            type="button"
            id="edLangEn"
            data-b_lang="en"
            data-testid="editor-lang-en"
            title="Düzenlenen dil: English"
            aria-pressed={state.editLocale === 'en'}
            onClick={() => dispatch({ type: 'edit_locale', locale: 'en' })}
          >
            English
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            id="saveDraftBtn"
            data-b_save="1"
            onClick={() => {
              void saveDraft();
            }}
          >
            Taslak Kaydet
          </button>
          <button
            className="btn btn-primary"
            type="button"
            id="publishTplBtn"
            data-b_pub="1"
            onClick={() => {
              /* Upstream saves silently first, so the gate publishes what the record holds — and
                 the gate opens only if that save actually landed, because the release is cut from
                 the STORED draft and a refused save would publish something else. */
              void saveDraft(true).then((saved) => {
                if (saved) setDialog('publish');
              });
            }}
          >
            Yayınla
          </button>
        </div>
      </div>

      <div className="editor">
        <div className="stage-wrap">
          <div className="stage-tools">
            <Readout
              live={live}
              layout={staged ?? layout}
              sel={state.sel}
              testData={state.testData}
              scale={scale}
            />
            <select
              className="select select-sm"
              id="zoomSel"
              aria-label="Zoom"
              style={{ width: 'auto' }}
              data-b_zoom="1"
              value={state.zoom === 'fit' ? 'fit' : String(state.zoom)}
              onChange={(e) =>
                dispatch({
                  type: 'zoom',
                  zoom: e.target.value === 'fit' ? 'fit' : Number.parseFloat(e.target.value),
                })
              }
            >
              <option value="fit">Ekrana sığdır</option>
              <option value="0.25">%25</option>
              <option value="0.5">%50</option>
              <option value="0.75">%75</option>
              <option value="1">%100</option>
            </select>
            <span id="fontStatus">
              <FontStatusPill rows={fontRows} />
            </span>
            <span className="toggles">
              {(
                [
                  ['guides', 'Merkez'],
                  ['safe', 'Güvenli alan'],
                  ['grid', 'Grid'],
                  ['snap', 'Snap'],
                ] as ['guides' | 'safe' | 'grid' | 'snap', string][]
              ).map(([key, label]) => (
                <label className="check" key={key}>
                  <input
                    type="checkbox"
                    id={`tg_${key}`}
                    checked={state[key]}
                    onChange={(e) => dispatch({ type: 'toggle', key, value: e.target.checked })}
                    {...{ [`data-b_${key}`]: '1' }}
                  />
                  {` ${label}`}
                </label>
              ))}
            </span>
            <span className="spacer" style={{ flex: 1 }} />
            <label className="btn btn-secondary btn-sm" htmlFor="bgUpload">
              Arka plan yükle (PNG/JPG/SVG)
            </label>
            <input
              type="file"
              id="bgUpload"
              accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
              hidden
              data-b_bg="1"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void onBgUpload(file);
              }}
            />
          </div>

          <div className="stage-outer" ref={outerRef}>
            {/* The reference makes the viewport focusable (`v.tabIndex = 0`) so the arrow keys can
                nudge the selected layer without a control to tab to, and the stage itself takes the
                pointer. Both elements are the acceptance target's own markup, role attributes and
                all — a role here would describe the canvas as something it is not. */}
            {/* biome-ignore-start lint/a11y/noNoninteractiveTabindex: see above */}
            {/* biome-ignore-start lint/a11y/noStaticElementInteractions: see above */}
            <div
              className="stage-viewport"
              id="stageViewport"
              data-b_key="1"
              tabIndex={0}
              onKeyDown={onViewportKeyDown}
              style={{
                width: `${Math.min(dispW, avail)}px`,
                height: `${Math.min(dispH, 720)}px`,
                overflow: dispW > avail || dispH > 720 ? 'auto' : 'hidden',
              }}
            >
              <div
                className="stage"
                id="stage"
                ref={stageRef}
                data-b_ptr="1"
                onPointerDown={onStagePointerDown}
                style={{ width: `${W}px`, height: `${H}px`, transform: `scale(${scale})` }}
              >
                <div
                  className="bg"
                  ref={bgRef}
                  dangerouslySetInnerHTML={{ __html: previewSVG(asRenderLayout(staged ?? layout), data) }}
                />
                <svg className="overlay" viewBox={`0 0 ${W} ${H}`} width={W} height={H} aria-hidden="true">
                  {state.grid ? (
                    <g stroke="rgba(0,0,0,.12)" strokeWidth={1 / scale}>
                      {gridLines}
                    </g>
                  ) : null}
                  {state.safe ? (
                    safeAreas.length ? (
                      safeAreas.map((a) => (
                        <g key={a.id}>
                          <rect
                            x={a.x * W}
                            y={a.y * H}
                            width={a.width * W}
                            height={a.height * H}
                            fill="none"
                            stroke="#F8BA3F"
                            strokeWidth={2 / scale}
                            strokeDasharray={`${12 / scale} ${8 / scale}`}
                          />
                          <text x={a.x * W + 6} y={a.y * H - 6} fontSize={14 / scale} fill="#B98A16">
                            {a.label}
                          </text>
                        </g>
                      ))
                    ) : (
                      /* No declared area: the reference still shows the 90% box, so the operator
                         sees the margin the layout is being judged against. */
                      <rect
                        x={W * 0.05}
                        y={H * 0.05}
                        width={W * 0.9}
                        height={H * 0.9}
                        fill="none"
                        stroke="#F8BA3F"
                        strokeWidth={2 / scale}
                        strokeDasharray={`${12 / scale} ${8 / scale}`}
                      />
                    )
                  ) : null}
                  {state.guides ? (
                    <g
                      stroke="#CF051E"
                      strokeOpacity=".55"
                      strokeWidth={1.5 / scale}
                      strokeDasharray={`${10 / scale} ${6 / scale}`}
                    >
                      <line x1={W / 2} y1="0" x2={W / 2} y2={H} />
                      <line x1="0" y1={H / 2} x2={W} y2={H / 2} />
                    </g>
                  ) : null}
                </svg>
                {resolved.map((r) => {
                  const l = r.layer;
                  const g = editorBoxGeometry(layout, r);
                  /* A right-aligned layer grows leftwards, so its handle sits on the left edge. */
                  const handleSide = l.align === 'right' ? 'left' : '';
                  const handleStyle: CSSProperties = {
                    width: `${Math.round(22 / scale)}px`,
                    height: `${Math.round(22 / scale)}px`,
                  };
                  if (handleSide) handleStyle.left = `-${Math.round(12 / scale)}px`;
                  else handleStyle.right = `-${Math.round(12 / scale)}px`;
                  const def = l.fieldKey
                    ? (layout.fieldDefinitions || []).find((f) => f.key === l.fieldKey)
                    : null;
                  const label =
                    r.kind === 'qr'
                      ? `QR · ${Math.round(l.width * W)}px`
                      : r.kind === 'image'
                        ? `Görsel · ${Math.round(l.width * W)}px`
                        : `${def ? fieldLabel(def, 'tr') : l.kind === 'static' ? 'Sabit metin' : l.fieldKey} · ${r.size}px${r.overflow ? ' · sığmıyor' : ''}${l.locked ? ' · kilitli' : ''}`;
                  return (
                    <div
                      key={l.id}
                      className={`layer-box ${l.id === state.sel ? 'is-selected' : ''} ${r.kind === 'text' && r.overflow ? 'is-overflow' : ''} ${l.locked ? 'is-locked' : ''}`}
                      data-layer={l.id}
                      style={{
                        left: `${g.left}px`,
                        top: `${g.top}px`,
                        width: `${g.w}px`,
                        height: `${g.h}px`,
                        borderWidth: `${borderWidth}px`,
                        ...(l.rotation ? { transform: `rotate(${l.rotation}deg)` } : {}),
                      }}
                    >
                      {/* Every handle and label is divided by the stage scale, so it keeps its
                          screen size however far the canvas is zoomed out. */}
                      <span className="tag" style={{ fontSize: `${Math.round(13 / scale)}px` }}>
                        {label}
                      </span>
                      {l.id === state.sel && !l.locked ? (
                        <>
                          <span className={`handle ${handleSide}`} style={handleStyle} />
                          <span
                            className="handle rot"
                            style={{
                              width: `${Math.round(20 / scale)}px`,
                              height: `${Math.round(20 / scale)}px`,
                              top: `-${Math.round(30 / scale)}px`,
                              left: `calc(50% - ${Math.round(10 / scale)}px)`,
                            }}
                          />
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* biome-ignore-end lint/a11y/noStaticElementInteractions: see above */}
            {/* biome-ignore-end lint/a11y/noNoninteractiveTabindex: see above */}
          </div>

          <div className="test-data">
            <div className="label">
              {'Test verileri '}
              <span className="small muted" style={{ fontWeight: 400 }}>
                — her alan için ayrı; değişince canvas anında güncellenir
              </span>
            </div>
            <div className="chips" id="nameChips">
              {chips.map((c) => (
                <button
                  className="chip"
                  type="button"
                  data-name={c}
                  key={c}
                  onClick={() => dispatch({ type: 'test_data', key: holderKey, value: c })}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="test-grid" id="testData">
              {usedFields.length ? (
                usedFields.map((f) => {
                  const def = (layout.fieldDefinitions || []).find((x) => x.key === f);
                  return (
                    <div className="field" key={f}>
                      <label htmlFor={`td_${f}`}>{def ? fieldLabel(def, 'tr') : f}</label>
                      <input
                        className="input input-sm"
                        id={`td_${f}`}
                        data-td={f}
                        value={state.testData[f] || ''}
                        autoComplete="off"
                        onChange={(e) => dispatch({ type: 'test_data', key: f, value: e.target.value })}
                      />
                    </div>
                  );
                })
              ) : (
                <p className="muted small">Şablonda dinamik alan yok.</p>
              )}
            </div>
          </div>

          <FitNotice live={live} layout={staged ?? layout} sel={state.sel} testData={state.testData} />

          {/* The canonical render of the LANGUAGE BEING EDITED: the same reduced view the stage
              draws, so «Sunucu render» rasterises the document that language would be sealed as. */}
          <ServerRenderOut
            template={template}
            layout={staged ?? layout}
            data={data}
            runId={renderRun}
            fontsReady={fontsReady}
          />
        </div>

        <aside className="card props" id="props" ref={propsRef}>
          <PropsPanel
            template={template}
            layout={layout}
            sel={state.sel}
            tab={state.tab}
            aspectLock={state.aspectLock}
            propsKey={propsKey}
            editLocale={state.editLocale}
            onSelect={(id) => dispatch({ type: 'select', id })}
            onTab={(tab) => dispatch({ type: 'tab', tab })}
            onAspectLock={(value) => dispatch({ type: 'aspect_lock', value })}
            onTestData={(key, value) => dispatch({ type: 'test_data', key, value })}
            onMutate={mutate}
            onEdit={liveEdit}
            onShowSafeAreas={() => dispatch({ type: 'toggle', key: 'safe', value: true })}
            onFontFile={(file, layer) => setFontFile({ file, layer })}
            assets={assets}
            fonts={fonts}
            library={libraryState}
          />
        </aside>
      </div>

      {dialog === 'preview' ? (
        <TemplatePreview
          template={template}
          layout={staged ?? layout}
          lang={state.editLocale}
          onClose={() => {
            setDialog(null);
            /* Looked at: the step the gate's row names, performed. */
            setPreviewReviewed(true);
          }}
        />
      ) : null}

      {dialog === 'publish' ? (
        <PublishGate
          template={template}
          layout={layout}
          previewReviewed={previewReviewed}
          onClose={() => setDialog(null)}
          onPublished={() => dispatch({ type: 'saved' })}
        />
      ) : null}

      {fontFile ? (
        <FontUploadModal
          file={fontFile.file}
          onCancel={() => setFontFile(null)}
          onSave={(meta) => {
            void saveFont(meta);
          }}
        />
      ) : null}

      {dialog === 'email' ? (
        <Modal label="E-posta" onClose={() => setDialog(null)}>
          {/* The dialog's own bar carries the title, so the panel does not repeat it here. */}
          <ModalHead title="Bu sertifikayla gönderilecek e-posta" onClose={() => setDialog(null)} />
          <div className="card-pad">
            <CertificateEmailPanel port={templateEmail} templateId={template.id} heading={null} />
          </div>
          <div className="card-foot">
            <button className="btn btn-ghost" data-close="1" type="button" onClick={() => setDialog(null)}>
              Kapat
            </button>
          </div>
        </Modal>
      ) : null}

      {dialog === 'linkedinCard' ? (
        <Modal label="LinkedIn görseli" onClose={() => setDialog(null)}>
          <ModalHead title="LinkedIn paylaşım görseli" onClose={() => setDialog(null)} />
          <div className="card-pad">
            <LinkedInCardPanel
              port={linkedInCard}
              templateId={template.id}
              heading={null}
              /*
               * The certificate as it is on the stage behind this dialog — the same call, the same
               * engine, the same sample values (rule 12). Not a placeholder and not the server's
               * last saved copy: what the author is looking at.
               *
               * Built HERE rather than in the panel, because this is where the producer is: the
               * raw-HTML audit checks that an injected string was composed by a registered
               * producer, and a panel handed a finished string could not show it one.
               *
               * `LINKEDIN_CARD_FIT` only overrides the root element's own sizing, so the viewBox
               * keeps the aspect and the drawing letterboxes inside whatever box it is given —
               * the same thing the server's composer does with it.
               */
              artwork={
                layout ? (
                  <div
                    data-testid="linkedin-card-artwork-drawing"
                    aria-hidden="true"
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      pointerEvents: 'none',
                    }}
                    dangerouslySetInnerHTML={{
                      /* The certificate the language being edited would be sealed as — the same
                         reduced view the stage draws, so the English card carries the English
                         document rather than the Turkish one under an English label. */
                      __html: previewSVG(asRenderLayout(staged ?? layout), data, LINKEDIN_CARD_FIT),
                    }}
                  />
                ) : null
              }
              certificateUnsaved={state.dirty}
              assets={assets}
              /* Keyed by the language so the panel starts clean on a switch: its own state is one
                 language's arrangement, and it re-reads that language from the server. */
              key={state.editLocale}
              lang={state.editLocale}
              onLang={(locale: 'tr' | 'en') => dispatch({ type: 'edit_locale', locale })}
              draft={linkedInDraft[state.editLocale] ?? null}
              onDraftChange={(next) => setLinkedInDraft(state.editLocale, next)}
            />
          </div>
          <div className="card-foot">
            <button className="btn btn-ghost" data-close="1" type="button" onClick={() => setDialog(null)}>
              Kapat
            </button>
          </div>
        </Modal>
      ) : null}

      {dialog === 'archive' ? (
        <Modal label="Arşivle" onClose={() => setDialog(null)}>
          <ModalHead title="Şablon arşivlensin mi?" onClose={() => setDialog(null)} />
          <div className="card-pad">
            <p>
              <b>{template.name}</b>
              {` arşivlenirse toplu üretimde ve tek kayıt ekranında seçilemez; daha önce üretilmiş ${fmtNum(tplCount(db, template.id))} kayıt kendi sürüm snapshot'larıyla yayında kalır.`}
            </p>
          </div>
          <div className="card-foot">
            <button className="btn btn-ghost" data-close="1" type="button" onClick={() => setDialog(null)}>
              Vazgeç
            </button>
            <button
              className="btn btn-dark"
              id="arcGo"
              type="button"
              onClick={() => {
                void setArchived(true);
                setDialog(null);
              }}
            >
              Arşivle
            </button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
